import { useMemo } from 'react';

import { useMutation, useQuery, UseQueryResult } from '@tanstack/react-query';
import { useBigQueryEventsTracking } from 'hooks/useBigQueryTracking';
import { trackMixpanelEvent } from 'utils/mixpanelClient';
import { getSolutionModelInfo } from 'utils/projectTypeMapping';

import { ChecklistTask, EChecklistTaskStatus, EPublisherSolutionModel, EPublisherSolutionModelSubType, Project } from '../common/contracts';
import { EEventsType, EQueryKeys } from '../constants/enums';
import useAxios from '../hooks/useAxios';

import { EApiRoutes, fetchConfig, getApiRouteOrMock } from './api.utils';
import { useUserDetails } from './useUserDetails';


const getCompletedTasksKey = (publisherId: string) =>
  `completed_tasks_${publisherId}`;

const getCompletedTasks = (publisherId: string): Set<string> => {
  const stored = localStorage.getItem(getCompletedTasksKey(publisherId));
  return stored ? new Set(JSON.parse(stored)) : new Set();
};

const saveCompletedTasks = (
  publisherId: string,
  completedTasks: Set<string>
) => {
  localStorage.setItem(
    getCompletedTasksKey(publisherId),
    JSON.stringify(Array.from(completedTasks))
  );
};

export const useChecklist = (currentPublisherId?: string | null, shouldFetchTasks: boolean = true) => {
  const axios = useAxios();
  const { trackBigQueryEvent } = useBigQueryEventsTracking();

  const { userDetails } = useUserDetails();
  const currentProject = useMemo(
    () => userDetails?.projects?.find(
      (project: Project) => project.publisherId === currentPublisherId
    ),
    [userDetails?.projects, currentPublisherId]
  );

  const projectIsOnboarding = useMemo(
    () => currentProject?.isOnboarding,
    [currentProject?.isOnboarding]
  );

  const isWebStore = useMemo(
    () => currentProject?.projectType === EPublisherSolutionModel.STORE,
    [currentProject?.projectType]
  );

  const isPaymentLinks = useMemo(
    () => currentProject?.projectType === EPublisherSolutionModel.CHECKOUT && 
           currentProject?.projectSubType === EPublisherSolutionModelSubType.PAYMENT_LINKS,
    [currentProject?.projectType, currentProject?.projectSubType]
  );


  const getTasks: UseQueryResult<ChecklistTask[]> = useQuery({
    ...fetchConfig.general,
    queryKey: [EQueryKeys.CHECKLIST_TASKS, currentPublisherId],
    queryFn: async () => {
      if (!userDetails?.isOnboarding && !projectIsOnboarding) {
        return [];
      }

      if (!currentPublisherId) {
        throw new Error('No publisher ID');
      }

      const response = await axios.get(
        getApiRouteOrMock(EApiRoutes.CHECKLIST_TASKS),
        {},
        currentPublisherId ? { 'x-publishers': currentPublisherId } : undefined
      );

      const tasks = response?.tasks as ChecklistTask[];

      try {
        const completedTasks = getCompletedTasks(currentPublisherId);

        tasks.forEach((task) => {
          const currentStatus = task.status;
          const taskKey = `${currentPublisherId}-${task.id}`;

          if (
            currentStatus === EChecklistTaskStatus.DONE &&
            !completedTasks.has(taskKey)
          ) {
            const solutionModelInfo = getSolutionModelInfo(isWebStore, isPaymentLinks);
            
            trackMixpanelEvent(
              EEventsType.DASHBOARD_SETUP_GUIDE_TASK_COMPLETED,
              {
                task_name: task.id,
                completion_time: new Date().toISOString(),
                task_id: task.id,
                publisher_id: currentPublisherId,
                solution_model: solutionModelInfo
              }
            );
            trackBigQueryEvent({
              eventName: EEventsType.DASHBOARD_SETUP_GUIDE_TASK_COMPLETED,
              taskName: task.id,
              taskStatus: EChecklistTaskStatus.DONE
            });
            completedTasks.add(taskKey);
          }
        });

        saveCompletedTasks(currentPublisherId, completedTasks);

        return tasks;
      } catch (error) {
        console.error('Error fetching tasks', error);
        return [];
      }
    },
    enabled: !!currentPublisherId && shouldFetchTasks
  });

  const updateTask = useMutation({
    mutationFn: async ({
      id,
      status
    }: {
      id: string;
      status: EChecklistTaskStatus;
    }) => {
      if (!currentPublisherId) {
        return [];
      }
      const response = await axios.put(
        `${getApiRouteOrMock(EApiRoutes.CHECKLIST_TASKS)}/${id}`,
        { status },
        currentPublisherId ? { 'x-publishers': currentPublisherId } : undefined
      );
      return response.data;
    }
  });

  return {
    getTasks,
    updateTask
  };
};
